import PropTypes from 'prop-types'
import { Button } from 'antd'
import { CheckCircleOutlined, FileSearchOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import { formatDOB } from '../../lib/datetime'
import { toPath } from '../../routes'
import filenameFromBlobPath from '../../lib/active-storage'
import DownloadLink from '../Common/DownloadLink'
import CircleCheckIcon from '../Common/CircleCheckIcon'
import PatientEntry from '../Common/PatientEntry'
import AuthorizedSwitch from './AuthorizedSwitch'

// Widths of antd's selection and expand gutter columns. The expanded details are
// indented by their sum so the fields line up with the card above them.
export const EXPAND_COLUMN_WIDTH = 40
export const SELECTION_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    // PatientEntry sets the link to nowrap for the desktop table; on mobile we
    // let a long name wrap instead of overflowing.
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  // Authorized switch + view link share the last line. Left-aligned rather than
  // space-between: the switch label swaps between "Authorized" and "Not
  // Authorized", so pinning it right made the edge jitter from row to row.
  actionsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 14,
    marginTop: 4,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#6b6b6b',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  spanTwo: {
    gridColumn: '1 / -1',
  },
})

function Field({ label, children = null, span = false }) {
  const classes = useStyles()
  return (
    <div
      className={span ? `${classes.field} ${classes.spanTwo}` : classes.field}
    >
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  span: PropTypes.bool,
}

// Mirrors the desktop Report/Transmission columns: a dash when unknown.
function FileStatus({ value = null, warningColor = undefined }) {
  if (value === null || value === undefined) return <span>-</span>
  return <CircleCheckIcon checked={value} falseStyle={{ color: warningColor }} />
}

FileStatus.propTypes = {
  value: PropTypes.bool,
  warningColor: PropTypes.string,
}

export function PreAuthorizationMobileCard({
  preAuthorization,
  isAdmin = false,
  warningColor = undefined,
  onAuthorizedChange,
}) {
  const classes = useStyles()
  const { device, patient, id } = preAuthorization
  const dob = patient?.birthDate ? formatDOB(patient.birthDate) : null
  const detailLine = [dob, device?.deviceModel?.deviceType?.name]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        {patient ? <PatientEntry patient={patient} /> : '—'}
      </div>
      {detailLine && <div className={classes.line}>{detailLine}</div>}
      {isAdmin && (
        <div className={classes.line}>
          Report{' '}
          <FileStatus
            value={preAuthorization.hasReportFile}
            warningColor={warningColor}
          />{' '}
          · Transmission{' '}
          <FileStatus
            value={preAuthorization.hasReportRawFile}
            warningColor={warningColor}
          />
        </div>
      )}
      <div className={classes.actionsLine}>
        <AuthorizedSwitch
          record={preAuthorization}
          handleAuthorizedChange={onAuthorizedChange}
        />
        <Link to={toPath.preAuthorizations.preAuthorization({ id })}>
          <Button
            type="link"
            size="small"
            icon={<FileSearchOutlined />}
            style={{ paddingLeft: 0 }}
          >
            View
          </Button>
        </Link>
      </div>
    </div>
  )
}

PreAuthorizationMobileCard.propTypes = {
  preAuthorization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    hasReportFile: PropTypes.bool,
    hasReportRawFile: PropTypes.bool,
    patient: PropTypes.shape({
      birthDate: PropTypes.string,
    }),
    device: PropTypes.shape({
      deviceModel: PropTypes.shape({
        deviceType: PropTypes.shape({
          name: PropTypes.string,
        }),
      }),
    }),
  }).isRequired,
  isAdmin: PropTypes.bool,
  warningColor: PropTypes.string,
  onAuthorizedChange: PropTypes.func.isRequired,
}

export function PreAuthorizationMobileDetails({ preAuthorization, indent = 0 }) {
  const classes = useStyles()
  const {
    cptCode,
    device,
    icd10Code,
    insuranceAuthorizationFilePath,
    insuranceAuthorizationNumber,
    notes,
    notesFilePath,
    patient,
    rejectedAt,
  } = preAuthorization

  let icmActive = '—'
  if (device?.icmCapable) {
    icmActive = <CircleCheckIcon checked={device.icmActive} />
  }

  const authNumber = insuranceAuthorizationFilePath ? (
    <DownloadLink path={insuranceAuthorizationFilePath}>
      <span>
        {insuranceAuthorizationNumber ||
          filenameFromBlobPath(insuranceAuthorizationFilePath)}
      </span>
    </DownloadLink>
  ) : (
    insuranceAuthorizationNumber || '—'
  )

  const notesValue = notesFilePath ? (
    <DownloadLink path={notesFilePath}>
      <span>{notes || filenameFromBlobPath(notesFilePath)}</span>
    </DownloadLink>
  ) : (
    notes || '—'
  )

  return (
    <div className={classes.detailsGrid} style={{ paddingLeft: indent }}>
      <Field label="MRN">{patient?.practiceMrn || '—'}</Field>
      <Field label="ICM active">{icmActive}</Field>
      <Field label="CPT code">{cptCode || '—'}</Field>
      <Field label="ICD-10 code">{icd10Code || '—'}</Field>
      <Field label="Tech DOS">
        {preAuthorization.billingDate
          ? formatDOB(preAuthorization.billingDate)
          : '—'}
      </Field>
      <Field label="ICM only">
        {rejectedAt ? <CheckCircleOutlined /> : '—'}
      </Field>
      <Field label="Auth #" span>
        {authNumber}
      </Field>
      <Field label="Notes" span>
        {notesValue}
      </Field>
    </div>
  )
}

PreAuthorizationMobileDetails.propTypes = {
  indent: PropTypes.number,
  preAuthorization: PropTypes.shape({
    cptCode: PropTypes.string,
    icd10Code: PropTypes.string,
    billingDate: PropTypes.string,
    notes: PropTypes.string,
    notesFilePath: PropTypes.string,
    insuranceAuthorizationNumber: PropTypes.string,
    insuranceAuthorizationFilePath: PropTypes.string,
    rejectedAt: PropTypes.string,
    patient: PropTypes.shape({
      practiceMrn: PropTypes.string,
    }),
    device: PropTypes.shape({
      icmCapable: PropTypes.bool,
      icmActive: PropTypes.bool,
    }),
  }).isRequired,
}
